import type { EmployerSnapshot, HourCorrectionInput, ImportResult } from '@klukka/client';
import { fmt, fmtDay, fmtH, fmtTs, type AdminLang } from '@klukka/core';
import { Ic } from '@klukka/ui';
import { useState } from 'react';
import { Fld, Panel, Select, type Tr } from '../widgets';

/**
 * Onboarding history and putting mistakes right.
 *
 * Two separate mechanisms, deliberately: an *amount* adjustment moves money
 * without pretending someone worked different hours, while an *hour* correction
 * rewrites the worklog and lets every downstream number recompute. Both leave
 * an audit entry, and hour corrections can be reverted to the previous value.
 */
export function ImporterTab({ snap, tr, lang, actions }: {
  snap: EmployerSnapshot;
  tr: Tr;
  lang: AdminLang;
  actions: {
    importCsv: (csv: string) => Promise<ImportResult>;
    addAdjustment: (input: { empId: string; date: string; amount: number; note: string }) => void;
    deleteAdjustment: (id: string) => void;
    correctHours: (input: HourCorrectionInput) => Promise<{ oldH: number; newH: number }>;
    deleteCorrection: (id: string) => void;
  };
}) {
  const active = snap.employees.filter((e) => !e.inactive);
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);

  const [empId, setEmpId] = useState(active[0]?.id ?? '');
  const [date, setDate] = useState(snap.serverToday);
  const [kind, setKind] = useState<'amount' | 'hours'>('amount');
  const [amount, setAmount] = useState(0);
  const [hoursDelta, setHoursDelta] = useState('');
  const [note, setNote] = useState('');
  const [message, setMessage] = useState<{ error: boolean; text: string } | null>(null);

  const nameOf = (id: string) => snap.employees.find((e) => e.id === id)?.name ?? id;

  const submit = async () => {
    setMessage(null);
    if (kind === 'hours') {
      const delta = parseFloat(hoursDelta.trim().replace(',', '.'));
      if (!Number.isFinite(delta) || delta === 0) {
        setMessage({ error: true, text: tr('invalidHours') });
        return;
      }
      try {
        const { oldH, newH } = await actions.correctHours({ empId, date, deltaHours: delta, note });
        // Spell out both the delta applied and the resulting total — showing only
        // one of them is what made an earlier version read as a wrong entry.
        setMessage({
          error: false,
          text: `${delta > 0 ? '+' : '−'}${fmtH(Math.abs(delta))} klst ⇒ ${fmtH(oldH)} → ${fmtH(newH)} klst · ${tr('recorded')}`,
        });
        setHoursDelta('');
        setNote('');
      } catch {
        setMessage({ error: true, text: tr('saveFailed') });
      }
      return;
    }
    if (!amount || !empId) return;
    actions.addAdjustment({ empId, date, amount, note });
    setAmount(0);
    setNote('');
  };

  const hourCorrections = snap.editLog.filter((l) => l.source === 'employer' || l.source === 'import');

  return (
    <div>
      <Panel title={tr('importTitle')}>
        <div className="dimtx" style={{ fontSize: 13, marginBottom: 8 }}>{tr('importHint')}</div>
        <textarea
          className="inp"
          rows={6}
          style={{ fontVariantNumeric: 'tabular-nums', resize: 'vertical' }}
          placeholder={'120388-2519;2026-07-03;8\n030592-3901;2026-07-03;9,5'}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
          <button className="btn-sm ok" onClick={() => void actions.importCsv(csv).then(setResult)}>
            <Ic.download size={14} /> {tr('parseApply')}
          </button>
          {result ? (
            <span style={{ fontSize: 13 }}>
              <b style={{ color: 'var(--ok)' }}>{result.imported}</b> {tr('importOk')}
              {result.skipped ? <span style={{ color: 'var(--danger)' }}> · {result.skipped} {tr('importErr')}</span> : null}
            </span>
          ) : null}
        </div>
        {result?.errors.length ? (
          <ul className="dimtx" style={{ fontSize: 12, marginTop: 8 }}>
            {result.errors.slice(0, 8).map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        ) : null}
      </Panel>

      <Panel title={tr('adjTitle')}>
        <div className="s-row" style={{ alignItems: 'flex-end' }}>
          <Select
            label={tr('name')}
            width="200px"
            value={empId}
            onChange={setEmpId}
            options={active.map((e) => ({ value: e.id, label: e.name }))}
          />
          <Fld label={tr('dayCol')} value={date} onChange={setDate} type="date" width="150px" />
          <label className="s-fld" style={{ width: 180 }}>
            {tr('typeLbl')}
            <div className="seg3" style={{ marginTop: 2 }}>
              <button className={kind === 'amount' ? 'on' : ''} onClick={() => { setKind('amount'); setMessage(null); }}>{tr('amountKind')}</button>
              <button className={kind === 'hours' ? 'on' : ''} onClick={() => { setKind('hours'); setMessage(null); }}>{tr('hoursKind')}</button>
            </div>
          </label>

          {kind === 'amount' ? (
            <Fld label={tr('amountLbl')} value={amount} onChange={setAmount} suffix="kr." width="170px" />
          ) : (
            <label className="s-fld" style={{ width: 170 }}>
              {tr('hoursDelta')}
              <span className="s-inp-wrap">
                <input className="inp" value={hoursDelta} onChange={(e) => setHoursDelta(e.target.value)} placeholder="-1,5" />
                <em>klst</em>
              </span>
            </label>
          )}

          <label className="s-fld" style={{ flex: 1, minWidth: 200 }}>
            {tr('noteLbl')}
            <input
              className="inp"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={lang === 'is' ? 'T.d. vangreidd yfirvinna í júlí' : 'E.g. underpaid July overtime'}
            />
          </label>

          <button className="btn-sm ok" style={{ height: 36 }} onClick={() => void submit()}>{tr('addAdj')}</button>
          {message ? (
            <span style={{ fontSize: 13, fontWeight: 600, color: message.error ? 'var(--danger)' : 'var(--ok)', alignSelf: 'center' }}>
              {message.text}
            </span>
          ) : null}
        </div>

        <div className="dimtx" style={{ fontSize: 12, margin: '10px 0' }}>{tr('adjMirror')}</div>

        {snap.adjustments.length === 0 ? <div className="dimtx" style={{ fontSize: 13 }}>{tr('empty')}</div> : null}
        {snap.adjustments.map((a) => (
          <div className="ap-row" key={a.id}>
            <div>
              <b>{nameOf(a.empId)}</b> · <span className="mono">{fmtDay(a.date)}</span> ·{' '}
              <b className="mono" style={{ color: a.amount >= 0 ? 'var(--ok)' : 'var(--danger)' }}>
                {a.amount >= 0 ? '+' : ''}{fmt(a.amount)}
              </b>
              {a.note ? <span className="dimtx"> · {a.note}</span> : null}
              <div className="trail">
                <div className="trail-ev">
                  <span className="trail-dot" style={{ background: 'var(--ok)' }} />
                  <span className="trail-main"><span>{a.who}</span><span className="trail-ts">{fmtTs(a.ts)}</span></span>
                </div>
              </div>
            </div>
            <span style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 'none' }}>
              <span className="dimtx mono" style={{ fontSize: 12 }}>{fmtTs(a.ts)}</span>
              <button className="btn-sm no" onClick={() => actions.deleteAdjustment(a.id)}>{tr('delEmp')}</button>
            </span>
          </div>
        ))}

        {hourCorrections.length ? (
          <div style={{ marginTop: 14 }}>
            <div className="p-title">{tr('hourFixes')}</div>
            {hourCorrections.map((l) => (
              <div className="ap-row" key={l.id}>
                <div>
                  <b>{nameOf(l.empId)}</b> · <span className="mono">{fmtDay(l.date)}</span> ·{' '}
                  <span className="mono">{fmtH(l.oldH)} → {fmtH(l.newH)} klst</span>{' '}
                  <b className="mono" style={{ color: l.newH >= l.oldH ? 'var(--ok)' : 'var(--danger)' }}>
                    ({l.newH >= l.oldH ? '+' : '−'}{fmtH(Math.abs(l.newH - l.oldH))})
                  </b>
                  <span className="dimtx"> · {l.who}{l.note ? ` — ${l.note}` : ''}</span>
                </div>
                <span style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 'none' }}>
                  <span className="dimtx mono" style={{ fontSize: 12 }}>{fmtTs(l.ts)}</span>
                  <button
                    className="btn-sm no"
                    title={`${tr('revertsTo')} ${fmtH(l.oldH)} klst`}
                    onClick={() => actions.deleteCorrection(l.id)}
                  >
                    {tr('delEmp')}
                  </button>
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </Panel>
    </div>
  );
}
